import { Button, ButtonBase, CircularProgress, Popover, Tooltip } from '@mui/material';
import { changelogStore } from '@stores/Changelog';
import { trackingStore } from '@stores/Tracking';
import MarkdownPreview from '@uiw/react-markdown-preview';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useState } from 'react';
import css from './ChangelogDot.module.scss';

/**
 * Changelog bobble, rendered next to the sidebar logo. The visible dot is 12px,
 * but it sits inside a 24px invisible ButtonBase so the tap target meets
 * touch-size minimums. Clicking it opens the changelog in a popover; closing the
 * popover marks the changelog read (persisted per newest-day hash in
 * localStorage, owned by `changelogStore`).
 *
 * Two states, and the dot never fully unmounts — having read the changelog is no
 * reason to lose the only route back to it:
 *   - UNREAD: red, pulsing, always visible. A notification.
 *   - READ: grey, no pulse, always visible but so faint it barely separates
 *     from the nav background; brightens on logo hover or keyboard focus. A
 *     quiet door for anyone who goes looking, near-silent for everyone else.
 * The feed loads at runtime, so until it has, and when it failed to, the dot is
 * in its READ look and the popover carries the loading or failure row instead.
 *
 * Known gap, and it's a real one: the collapsed rail hides `.logoContainer`
 * outright (`Nav.module.scss`), and this renders inside it — so on the collapsed
 * rail there are ZERO changelog entry points, the unread notification included.
 * Collapsed is persisted to localStorage (`Nav.tsx` `readOpened`), so a user who
 * collapses the rail once never sees the changelog again, in any session. That
 * predates the read state, so this component didn't cause it and can't fix it
 * from here: the durable fix is an entry point that survives the rail, e.g. a
 * "What's new" row in the account footer popover, which already renders
 * collapsed. See `.claude/docs/changelog-entry-points.md`.
 *
 * Ported from the chat frontend (monorepo.vibedit `SideBar/ChangelogDot.tsx`) —
 * keep the two in sync where practical. Two deliberate divergences: there the
 * dot is a CHILD of the logo, so its clicks bubble into the logo's
 * navigate-to-Workspace handler and have to be contained, while here it's a
 * SIBLING of the logo (ours takes no children, and is shared with the Auth
 * pages), so it sits outside the logo's click area entirely; and there the dot
 * unmounts once read and while the feed is missing, while here it stays.
 */
export const ChangelogDot = observer(function ChangelogDot() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { markdown, hasUnread, complete, fetching, error } = changelogStore;

  return (
    <>
      <Tooltip title="What's new" placement="right">
        <ButtonBase
          type="button"
          // `revealed` keeps the read-state dot brightened while its own popover
          // is open; neither hover nor focus survives the popover opening, so
          // without it the anchor dims from under it. See ChangelogDot.module.scss.
          className={classNames(css.dotButton, {
            [css.read]: !hasUnread,
            [css.revealed]: !!anchor,
          })}
          aria-label="What's new"
          onClick={(e) => {
            // Read BEFORE `setAnchor`: closing the popover flips `hasUnread`
            // false, so this is the only point where the two states are still
            // distinguishable.
            trackingStore.changelogViewed({ unread: hasUnread });
            setAnchor(e.currentTarget);
          }}
        >
          <span className={css.dot} />
        </ButtonBase>
      </Tooltip>

      {anchor && (
        <Popover
          // The popover's DOM portals to <body>, but React synthetic events
          // still bubble through the React TREE — which lands them in the nav
          // header, right next to the logo's navigate-to-Workspace handler.
          // Nothing up there listens today; this keeps it that way for free if
          // the header ever gains a click handler.
          onClick={(e) => e.stopPropagation()}
          open={!!anchor}
          onClose={() => {
            setAnchor(null);
            changelogStore.markRead();
          }}
          anchorEl={anchor}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: -16 }}
          slotProps={{
            paper: { className: css.changeLog },
          }}
        >
          {/* Build/commit id above the changelog. Near-invisible by design — it's a
              copy target for support/debugging, readable only once selected. */}
          <div className={css.version}>{process.env.VERSION}</div>
          {markdown && (
            <MarkdownPreview
              source={markdown}
              style={{ backgroundColor: 'transparent', color: 'inherit' }}
              wrapperElement={{ 'data-color-mode': 'dark' }}
            />
          )}
          {/* A failed "Load all" keeps the head above and shows the failure row below it. */}
          {error && (
            <div className={css.status}>
              Couldn&apos;t load what&apos;s new.
              <Button variant="outlined" size="small" onClick={() => changelogStore.retry()}>
                Try again
              </Button>
            </div>
          )}
          {!error && !markdown && (
            <div className={css.status}>
              <CircularProgress size={20} />
            </div>
          )}
          {!error && markdown && !complete && (
            <div className={css.status}>
              <Button
                fullWidth
                variant="outlined"
                loading={fetching}
                onClick={() => changelogStore.loadAll()}
              >
                Load all
              </Button>
            </div>
          )}
        </Popover>
      )}
    </>
  );
});
